<template>
  <Bar :id="chartId" :options="mergedOptions" :data="chartData" />
</template>

<script setup>
// Single bar-chart wrapper that supports both orientations and optional
// annotation lines. It replaces the previous BarChart.vue (vertical) and
// HorizontalBarChart.vue (horizontal + annotations) components.
//
// Chart.js registration happens once at app bootstrap (see
// Charts/base/registerChartjs.js), so no registration is needed here.
import { Bar } from "vue-chartjs";
import { computed } from "vue";

// Module level counter guaranteeing a unique canvas id per rendered chart.
let uid = 0;

const props = defineProps({
  chartData: {
    type: Object,
    required: true,
  },
  options: {
    type: Object,
    required: true,
  },
  annotations: {
    type: Array,
    required: false,
    default: () => [],
  },
  aspectRatio: {
    type: Object,
    required: false,
    default: null,
  },
  chartId: {
    type: String,
    required: false,
    default: null,
  },
});

const chartId = computed(() => props.chartId ?? `bar-chart-${++uid}`);

const annotationColors = ["#ff0000", "#ff8000", "#e45700"];

// Merge any provided annotation lines into the chart options.
const mergedOptions = computed(() => {
  if (!props.annotations || props.annotations.length === 0) {
    return props.options;
  }

  const chartAnnotations = props.annotations.reduce((acc, item, index) => {
    acc[`line${index}`] = {
      type: "line",
      xMin: item.value,
      xMax: item.value,
      borderColor: annotationColors[index] || "#e49300",
      borderWidth: 2,
      label: {
        content: item.label,
        enabled: true,
        position: "top",
      },
    };
    return acc;
  }, {});

  return {
    ...props.options,
    plugins: {
      ...props.options.plugins,
      annotation: {
        annotations: chartAnnotations,
      },
    },
  };
});
</script>

<style scoped></style>
